import { storeToRefs } from 'pinia'

import { useBookingStore } from '@/store/booking'
import { useAuthStore } from '@/store/auth'
import { useGlobalModalStore } from '@/store/global-modal'
import { useBranchStore } from '@/store/branch'

import type { BranchData, FbranchDiscountSlot } from '@/types/api'
import type { GroupByDateSlots, SlotInfo } from '@/types/product'
import { GtmEvent } from '@/types/gtm'

import { getMemberSuspended } from '@/service/member'
import { getBranchBLockStatus } from '@/service/branch'

interface UseReservationNextStepArgs {
  branch: ReadonlyRef<Nullable<BranchData>>
  attendance: ReadonlyRef<number>
  date: ReadonlyRef<Nullable<string>>
  slotInfo: ReadonlyRef<Nullable<SlotInfo>>
  format(...args: any[]): string
}

export function useReservationSuspense() {
  const { t } = useI18n()

  const globalModalStore = useGlobalModalStore()

  const { handleOpen } = useCustomerService()

  const { isLoading, execute } = useAsyncState(getMemberSuspendedExecutor, null, {
    immediate: false,
    throwError: true,
  })

  async function getMemberSuspendedExecutor() {
    try {
      const { data } = await getMemberSuspended()

      return data.is_suspended
    } catch {
      // PM 說 API 失敗時不阻止流程
      return false
    }
  }

  function openSuspendedModal() {
    globalModalStore.showModal({
      body: t('branch_detail.suspend.content'),
      confirmText: t('general.contact_us'),
      cancelText: t('general.got_it'),
      onConfirm: handleOpen,
    })
  }

  return { isLoading, openSuspendedModal, checkMemberSuspended: execute.bind(null, 0) }
}

export function useRestaurantBlockStatus(bid: string | number) {
  const { t } = useI18n()

  const globalModalStore = useGlobalModalStore()

  const { isLoading, execute } = useAsyncState(getRestaurantBlockStatus, null, {
    immediate: false,
    throwError: true,
  })

  async function getRestaurantBlockStatus() {
    try {
      const { data } = await getBranchBLockStatus(bid)

      return data.is_blocked
    } catch {
      // PM 說 API 失敗時不阻止流程
      return false
    }
  }

  function openBlockedModal() {
    globalModalStore.showModal({
      body: t('blocked_user_dialog'),
      confirmText: t('general.confirm'),
    })
  }

  return { isLoading, openBlockedModal, checkBranchBlockStatus: execute.bind(null, 0) }
}

export function useReservationNextStep(args: UseReservationNextStepArgs) {
  const { branch, attendance, date, slotInfo, format } = args

  const nuxtApp = useNuxtApp()

  const router = useLocaleRouter()
  const { isLoading: isPageIndicatorLoading } = useLoadingIndicator()

  const bookingStore = useBookingStore()
  const {
    branch: storeBranch,
    bookingAttendance: storeAttendance,
    bookingDate: storeDate,
    selectedSlot: storeSelectedSlot,
  } = storeToRefs(bookingStore)
  const authStore = useAuthStore()

  const branchStore = useBranchStore()
  const { hasGoogleAiSummary, hasFunNowAiSummary } = storeToRefs(branchStore)

  const { isLoading: isGetMemberSuspendedLoading, checkMemberSuspended, openSuspendedModal } = useReservationSuspense()
  const { isLoading: isGetBranchBlockStatusLoading, checkBranchBlockStatus, openBlockedModal } = useRestaurantBlockStatus(branch.value?.bid ?? 0)

  const isReservationNextStepLoading = computed(() => isGetMemberSuspendedLoading.value || isGetBranchBlockStatusLoading.value)

  function updateBookingStore() {
    storeBranch.value = branch.value
    storeAttendance.value = attendance.value
    storeDate.value = date.value!
    storeSelectedSlot.value = slotInfo.value
  }

  async function redirectToRemarksPage() {
    updateBookingStore()
    await router.push({ path: `/booking/${branch.value?.bid}/remarks` })
  }

  async function processReservationNextStep() {
    if (isPageIndicatorLoading.value) {
      return
    }
    if (!authStore.isLogin) {
      return authStore.openLoginModal()
    }

    const checkStatusPromises = [checkMemberSuspended(), checkBranchBlockStatus()]

    const [isSuspended, isBlocked] = await Promise.all(checkStatusPromises)

    if (isSuspended) return openSuspendedModal()
    if (isBlocked) return openBlockedModal()

    const [hashedEmail, hashedPhone] = await Promise.all([
      authStore.user?.email ? hashString(authStore.user.email) : '',
      authStore.user?.phone ? hashString(authStore.user.phone) : '',
    ])
    const bookingTime = format(new Date(slotInfo.value!.slot!), 'yyyy-MM-dd HH:mm:ss')
    nuxtApp.$trackEvent({
      event: GtmEvent.TIME_SELECTION_COMPLETED,
      payload: {
        hashedEmail,
        hashedPhone,
        branch: branch.value,
        bookingTime: bookingTime,
        pax: attendance.value,
        discount: slotInfo.value?.discount,
        hasGoogleAiSummary: hasGoogleAiSummary.value,
        hasFunNowAiSummary: hasFunNowAiSummary.value,
      },
    })

    await redirectToRemarksPage()
  }

  return { isReservationNextStepLoading, processReservationNextStep }
}

interface UseReservationStateArgs {
  isViewerShown: ReadonlyRef<boolean>
  selectedTimeSlotInfo: Ref<Nullable<FbranchDiscountSlot | undefined>>
  selectedDate: Ref<Nullable<string>>
  groupedByDateSlots: ReadonlyRef<GroupByDateSlots>
}

/** 處理預訂的人數、日期、折扣時間邏輯 */
export function useReservationState(args: UseReservationStateArgs) {
  const { isViewerShown, selectedTimeSlotInfo, selectedDate, groupedByDateSlots } = args

  watch(groupedByDateSlots, validateAndSelectTimeSlot)

  watch(selectedDate, newSelectedDate => {
    if (!isViewerShown.value || !newSelectedDate) {
      return
    }

    selectNewTimeSlot()
  })

  function selectNewTimeSlot() {
    if (!selectedDate.value || !selectedTimeSlotInfo.value?.slot || !selectedTimeSlotInfo.value.discount) {
      return
    }

    const [, selectedSlotTime] = selectedTimeSlotInfo.value.slot.split(' ')
    const availableSlotInfo = groupedByDateSlots.value[selectedDate.value]?.slots
    const newTimeSlot = availableSlotInfo.find(slotInfo => {
      const [, slotTime] = slotInfo.slot?.split(' ') || []

      return slotTime === selectedSlotTime && slotInfo.discount === selectedTimeSlotInfo.value?.discount
    })

    if (newTimeSlot?.slot !== selectedTimeSlotInfo.value.slot) {
      selectedTimeSlotInfo.value = newTimeSlot
    }
  }

  function validateAndSelectTimeSlot() {
    if (!selectedDate.value) {
      return
    }

    if (selectedDate.value in groupedByDateSlots.value) {
      selectNewTimeSlot()
    } else {
      selectedDate.value = null
      selectedTimeSlotInfo.value = undefined
    }
  }

  return {}
}
